import React, { useEffect, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepConnector from '@material-ui/core/StepConnector';
import { Grid } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';

import BuildIcon from '@material-ui/icons/Build';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import SnoozeIcon from '@material-ui/icons/Snooze';
import EmojiTransportationIcon from '@material-ui/icons/EmojiTransportation';
import VisibilityIcon from '@material-ui/icons/Visibility';

import SingleDocumentViewer from '../../../../../Core/Controls/GenericFileUploader/DocumentViewer';
import { downloadUploadedFile } from '../../../../../Core/Services/FileUploaderService';
import { getConfigIdFromName } from '../../../../../Core/Controls/GenericFileUploader/FileUploaderConfig';
import DialogComp from '../../../../../Core/Dialog/DialogComp';
import { useToast } from '../../../../../Core/Hooks/useToast';

import { getVehicleContractHistory_GetProgressDetails } from '../../../../../Core/Services/InternalUser/InternalDashService';

// -------------------- Connector --------------------

const ColorlibConnector = withStyles({
    alternativeLabel: { top: 15 },
    active: {
        '& $line': {
            backgroundImage: 'linear-gradient(95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)'
        }
    },
    completed: {
        '& $line': {
            backgroundImage: 'linear-gradient(95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)'
        }
    },
    line: {
        height: 5,
        border: 0,
        backgroundColor: '#eaeaf0',
        borderRadius: 1
    }
})(StepConnector);

// -------------------- Step Icon --------------------

const useStepIconStyles = makeStyles({
    root: {
        backgroundImage: 'linear-gradient(95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)',
        zIndex: 1,
        color: '#fff',
        width: 35,
        height: 35,
        display: 'flex',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center'
    }
});

function StepIconComponent(props) {
    const classes = useStepIconStyles();

    const icons = {
        1: <BuildIcon style={{ fontSize: 16 }} />,
        2: <LocalShippingIcon style={{ fontSize: 16 }} />,
        3: <EmojiTransportationIcon style={{ fontSize: 16 }} />,
        4: <BuildIcon style={{ fontSize: 16 }} />,
        5: <EmojiTransportationIcon style={{ fontSize: 16 }} />,
        6: <SnoozeIcon style={{ fontSize: 16 }} />,
        7: <BuildIcon style={{ fontSize: 16 }} />,
        8: <LocalShippingIcon style={{ fontSize: 16 }} />,
        9: <BuildIcon style={{ fontSize: 16 }} />
    };

    return <div className={classes.root}>{icons[String(props.icon)]}</div>;
}

// -------------------- Styles --------------------

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
        '& .MuiStepper-root': {
            padding: '10px 5px'
        }
    },
    stepLabel: {
        fontSize: 14,
        fontWeight: 400 // Normal weight for step title
    },
    subText: {
        fontSize: 10,
        fontWeight: 700 // Bold for details
    },
    loaderWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 200
    }
}));

// -------------------- Component --------------------

const ContractHistoryProgressBar = ({ historyId }) => {
    const classes = useStyles();
    const { showToastError } = useToast();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [documentState, setDocumentState] = useState({
        showDocument: false,
        imageList: [],
        docType: ''
    });

    useEffect(() => {
        async function fetchProgress() {
            if (historyId) {
                setLoading(true);

                const res = await getVehicleContractHistory_GetProgressDetails(historyId);

                if (res.success && res.data?.details?.table?.length > 0) {
                    setData(res.data.details.table[0]);
                } else {
                    setData(null);
                }

                setLoading(false);
            }
        }

        fetchProgress();
    }, [historyId]);

    const safe = (val) => val || 'Not set';

    const ViewDownloadFile = async (fileName, docType) => {
        setDocumentState((st) => ({
            ...st,
            showDocument: true,
            docType
        }));

        let success = await downloadUploadedFile(
            getConfigIdFromName('VehicleContracts'),
            data.vehicleContractID,
            fileName,
            `${docType}.pdf`,
            false,
            false,
            '.pdf'
        );

        if (success) {
            setDocumentState((st) => ({
                ...st,
                imageList: success
            }));
        } else {
            showToastError("Something went wrong, can't download file.");
        }
    };

    if (loading) {
        return (
            <div className={classes.loaderWrapper}>
                <CircularProgress size={40} />
            </div>
        );
    }

    if (!data) {
        return <div className={classes.loaderWrapper}>No history data found.</div>;
    }

    const steps = [
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step1)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.summerServiceDue)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.summerServicCompleted)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step2)}

                        {data.deliveryDocumentID && (
                            <VisibilityIcon
                                style={{ fontSize: 14, marginLeft: 6, cursor: 'pointer' }}
                                onClick={() => ViewDownloadFile(data.deliveryDocumentID, 'DELIVERY')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.deliveryDate)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.deliveryCollectionDate)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step3)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.deliveredBy)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.onsiteDate)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step4)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.onhireServiceDue)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.onhireServiceCompleted)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step5)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.onhireDate)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.winterEndDate)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step6)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.offhireSetDate)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.offhireDate)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step7)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.offhireServiceDue)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.offhireServiceCompleted)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step8)}

                        {data.collectionDocumentID && (
                            <VisibilityIcon
                                style={{ fontSize: 14, marginLeft: 6, cursor: 'pointer' }}
                                onClick={() => ViewDownloadFile(data.collectionDocumentID, 'COLLECTION')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.collectionDate)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.collectionStatus)}</small>
                    </Grid>
                </Grid>
            )
        },
        {
            label: (
                <Grid container>
                    <Grid item xs={12} className={classes.stepLabel}>
                        {safe(data.step9)}
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.factoryInspectionDue)}</small>
                    </Grid>
                    <Grid item xs={12}>
                        <small className={classes.subText}>{safe(data.offhireServiceCompleted1)}</small>
                    </Grid>
                </Grid>
            )
        }
    ];

    return (
        <>
            <div className={classes.root}>
                <Stepper alternativeLabel activeStep={steps.length} connector={<ColorlibConnector />}>
                    {steps.map((step, index) => (
                        <Step key={index} completed>
                            <StepLabel StepIconComponent={StepIconComponent}>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </div>

            {documentState.showDocument && (
                <DialogComp
                    title={`Document - ${documentState.docType}`}
                    onClose={() =>
                        setDocumentState({
                            showDocument: false,
                            imageList: [],
                            docType: ''
                        })
                    }
                    maxWidth="xl"
                    fullWidth
                    fullScreen
                >
                    <SingleDocumentViewer imageList={documentState.imageList || []} slectedFileExt={'.pdf'} />
                </DialogComp>
            )}
        </>
    );
};

export default ContractHistoryProgressBar;
